import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import ToolResultCard from "@/components/tools/ToolResultCard"
import { NumberField, SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, NumberedSteps, FeatureGrid, TierCards, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-money-calculator")!

const NICHES = [
  { value: "general-lifestyle", label: "General Lifestyle", multiplier: 1.0 },
  { value: "fashion-beauty", label: "Fashion & Beauty", multiplier: 1.3 },
  { value: "fitness-health", label: "Fitness & Health", multiplier: 1.2 },
  { value: "tech-gaming", label: "Technology & Gaming", multiplier: 1.5 },
  { value: "finance-business", label: "Finance & Business", multiplier: 2.0 },
  { value: "food-cooking", label: "Food & Cooking", multiplier: 1.1 },
  { value: "travel-adventure", label: "Travel & Adventure", multiplier: 1.2 },
  { value: "parenting-family", label: "Parenting & Family", multiplier: 1.0 },
] as const

export const Route = createFileRoute("/tools/instagram-money-calculator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-money-calculator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-money-calculator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-money-calculator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-money-calculator" },
      ]}),
      createHowToSchema({ name: "How to Estimate Instagram Earnings", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: MoneyCalculatorPage,
})

function MoneyCalculatorPage() {
  const [followers, setFollowers] = useState("")
  const [engagementRate, setEngagementRate] = useState("")
  const [niche, setNiche] = useState("general-lifestyle")
  const [result, setResult] = useState<{
    perPost: number
    perStory: number
    monthlyPotential: number
    tier: string
  } | null>(null)

  const calculate = () => {
    const f = Number(followers)
    const er = Number(engagementRate)
    if (!f || f < 100 || !er) return

    const nicheData = NICHES.find((n) => n.value === niche) || NICHES[0]
    const baseRate = 15 // $15 per 1000 followers midpoint
    const perPost = (f * (er / 100) * nicheData.multiplier) / 1000 * baseRate
    const perStory = perPost * 0.6
    const monthlyPotential = (perPost * 4) + (perStory * 28)

    let tier: string
    if (f >= 1_000_000) tier = "Macro (1M+)"
    else if (f >= 100_000) tier = "Mid-Tier (100K-1M)"
    else if (f >= 10_000) tier = "Micro (10K-100K)"
    else tier = "Nano (1K-10K)"

    setResult({ perPost, perStory, monthlyPotential, tier })
  }

  const reset = () => {
    setFollowers("")
    setEngagementRate("")
    setNiche("general-lifestyle")
    setResult(null)
  }

  const fmt = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`
    return `$${n.toFixed(0)}`
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          {/* Section 1: How Much Do Instagram Creators Earn? */}
          <ContentSection>
            <SectionHeading>How Much Do Instagram Creators Earn?</SectionHeading>
            <Paragraph className="mb-3">
              The Instagram creator economy has exploded into a multi-billion dollar industry in 2026. With over 2 billion monthly active users and a platform that increasingly favors short-form video, Reels, and interactive Stories, creators have more monetization opportunities than ever before. Whether you are a nano-influencer just starting out or a macro creator with millions of followers, Instagram remains one of the most lucrative platforms for turning content into income.
            </Paragraph>
            <Paragraph className="mb-3">
              Earnings on Instagram range wildly — from as little as $10 per sponsored post for small creators to $100,000 or more for celebrity-level accounts with massive, engaged audiences. The average mid-tier influencer with 100K to 500K followers can realistically earn between $2,000 and $8,000 per sponsored feed post, with additional revenue from Stories, Reels, affiliate links, and digital products stacking on top.
            </Paragraph>
            <Paragraph className="mb-3">
              However, the single most important takeaway for any aspiring creator is this: <strong>engagement matters far more than follower count</strong>. A creator with 15,000 highly engaged followers in a profitable niche like finance or SaaS can out-earn someone with 200,000 passive followers in a general lifestyle niche. Brands in 2026 are data-savvy — they track engagement rates, story reply rates, DM conversion rates, and audience demographics before committing budget. The creators who respond to every comment, reply to every DM, and build real community around their content are the ones commanding premium rates.
            </Paragraph>
            <Paragraph>
              Our Instagram Money Calculator below uses industry-standard CPM rates, niche multipliers, and engagement weighting to give you a realistic estimate of your earning potential. Whether you are negotiating your first brand deal or benchmarking your rates against the market, this tool provides the data you need to price yourself confidently.
            </Paragraph>
          </ContentSection>

          {/* Section 2: Instagram Earnings by Follower Count */}
          <ContentSection>
            <SectionHeading>Instagram Earnings by Follower Count</SectionHeading>
            <Paragraph className="mb-4">
              The table below breaks down typical Instagram creator earnings by follower tier. These figures represent industry averages for 2026 and assume a standard engagement rate (2-4%). Creators with above-average engagement or in premium niches can expect rates at the higher end or beyond these ranges.
            </Paragraph>
            <TierCards tiers={[
              { label: "Nano", range: "1K-10K", description: "$10-$100/post, $5-$50/story", color: "green" },
              { label: "Micro", range: "10K-100K", description: "$100-$1,000/post, $50-$500/story", color: "blue" },
              { label: "Mid-Tier", range: "100K-1M", description: "$1,000-$10,000/post, $500-$5,000/story", color: "yellow" },
              { label: "Macro", range: "1M+", description: "$10,000+/post, $5,000+/story", color: "green" },
            ]} />
          </ContentSection>

          {/* Section 3: How to Calculate Instagram Earnings */}
          <ContentSection>
            <SectionHeading>How to Calculate Instagram Earnings</SectionHeading>
            <Paragraph className="mb-4">
              Estimating your Instagram earning potential does not require guesswork. Follow these four steps to get a data-driven estimate using our calculator above.
            </Paragraph>
            <NumberedSteps steps={[
              {
                title: "Enter Your Follower Count",
                description: "Start by entering your current Instagram follower count. This is the baseline metric that brands use to categorize creators into tiers. You can find your exact follower count on your Instagram profile or in the Professional Dashboard if you have a creator or business account. Our calculator accepts any count from 100 followers and up — even if you are just getting started, it is valuable to understand your current earning potential and set growth targets.",
              },
              {
                title: "Calculate Your Engagement Rate",
                description: "Your engagement rate is the percentage of your followers who actively interact with your content through likes, comments, shares, and saves. To calculate it manually, add up the total likes and comments on your last 10-12 posts, divide by your follower count multiplied by the number of posts, and multiply by 100. A higher engagement rate significantly increases your estimated earnings because brands pay a premium for audiences that actually pay attention.",
              },
              {
                title: "Select Your Niche",
                description: "Your content niche dramatically impacts how much brands are willing to pay. The calculator applies niche-specific multipliers based on real market data. Finance and business content commands up to 2x the standard rate because financial products have high customer lifetime values. Technology and gaming content earns roughly 1.5x standard rates. Fashion, beauty, fitness, health, and travel niches earn 1.2-1.3x standard rates. General lifestyle and parenting content tends to earn at baseline rates. Selecting the right niche ensures your estimate reflects what brands in your specific industry are actually paying in 2026.",
              },
              {
                title: "Review Your Earnings Estimate",
                description: "After clicking \"Calculate Earnings,\" you will see three key figures: your estimated rate per sponsored post, your estimated rate per Story or Reel, and your total monthly earning potential. The monthly potential assumes roughly 4 sponsored posts and 28 Stories per month — a realistic cadence for active creators. Use these numbers as a starting point for brand negotiations. Keep in mind that actual rates can vary based on content quality, audience demographics (US and UK audiences command higher rates), exclusivity requirements, and usage rights. Creators who respond quickly to brand inquiries via DM and maintain professional media kits tend to close deals at the higher end of these ranges.",
              },
            ]} />
          </ContentSection>

          {/* Section 4: 7 Ways Instagram Creators Make Money */}
          <ContentSection>
            <SectionHeading>7 Ways Instagram Creators Make Money</SectionHeading>
            <Paragraph className="mb-4">
              Sponsored posts are just the tip of the iceberg. Successful Instagram creators in 2026 diversify across multiple revenue streams to build stable, scalable income. Here are the seven most common ways creators monetize their audience.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              {
                icon: "\u{1F4B0}",
                title: "Sponsored Posts",
                description: "The most visible form of creator income. Brands pay you to feature their product or service in a feed post, Reel, or carousel. Rates depend on your follower count, engagement rate, and niche. In 2026, Reels typically command a 20-50% premium over static posts due to their higher reach and algorithmic distribution.",
              },
              {
                icon: "\u{1F517}",
                title: "Affiliate Marketing",
                description: "Earn a commission (typically 5-30%) every time someone purchases through your unique affiliate link. This works especially well in Stories with swipe-up links and in DM automations that deliver links instantly when followers comment a keyword. Affiliate income scales with engagement and trust.",
              },
              {
                icon: "\u{1F6CD}\uFE0F",
                title: "Selling Products",
                description: "Many creators launch their own product lines — merchandise, skincare, supplements, or curated boxes. Instagram Shopping integration makes it seamless to tag products in posts and Stories. Creators with strong personal brands can achieve significantly higher margins than they would from sponsorships alone.",
              },
              {
                icon: "\u{1F4DA}",
                title: "Digital Products & Courses",
                description: "E-books, templates, presets, online courses, and membership communities are high-margin revenue streams that require upfront effort but generate passive income over time. A fitness creator might sell a $47 workout plan to thousands of followers. The key is packaging your expertise into a product your audience already wants.",
              },
              {
                icon: "\u2B50",
                title: "Instagram Subscriptions",
                description: "Instagram's native Subscriptions feature lets creators charge $0.99-$99.99 per month for exclusive content, Stories, Lives, and badges. This provides predictable recurring revenue. Even a modest subscriber base of 200 people at $4.99/month generates nearly $1,000 in monthly recurring revenue.",
              },
              {
                icon: "\u{1F393}",
                title: "Coaching & Consulting",
                description: "Creators with specialized expertise can offer one-on-one coaching or consulting services. Business coaches, fitness trainers, marketing consultants, and career advisors frequently use Instagram as a top-of-funnel channel, converting followers into high-ticket coaching clients via DMs. Rates range from $100 to $500+ per hour.",
              },
              {
                icon: "\u{1F91D}",
                title: "Brand Ambassadorships",
                description: "Unlike one-off sponsored posts, brand ambassadorships are long-term partnerships (3-12 months) where you represent a brand consistently. These deals typically pay 2-5x more than individual posts because they include exclusivity, multiple content deliverables, and often performance bonuses.",
              },
            ]} />
          </ContentSection>

          {/* Section 5: 5 Tips to Maximize Your Instagram Earnings */}
          <ContentSection>
            <SectionHeading>5 Tips to Maximize Your Instagram Earnings</SectionHeading>
            <Paragraph className="mb-4">
              Earning more on Instagram is not just about growing your follower count. The creators who earn the most per follower are strategic about how they build, engage, and monetize their audience. Here are five proven strategies to increase your income.
            </Paragraph>
            <TipList tips={[
              {
                title: "Focus on engagement over followers",
                description: "A 10K account with 6% engagement is more valuable to brands than a 100K account with 0.5% engagement. Reply to every comment, ask questions in captions, use interactive Story stickers, and create content that sparks conversation. Higher engagement directly translates to higher rates.",
              },
              {
                title: "Diversify your revenue streams",
                description: "Do not rely on sponsorships alone. Layer affiliate marketing, digital products, subscriptions, and consulting on top of brand deals. Creators with 3+ income streams earn 2-4x more than those who depend on a single source. Diversification also protects you from algorithm changes or shifts in brand budgets.",
              },
              {
                title: "Automate your DMs",
                description: "Every missed DM is lost revenue. When a follower messages you about a product recommendation, a coaching inquiry, or a link to something you mentioned, responding instantly is critical. Tools like Zapify automate DM responses so that every inquiry gets an immediate, personalized reply — even while you sleep. Automated DMs can deliver affiliate links, booking pages, and lead magnets at scale, turning passive followers into paying customers 24/7.",
              },
              {
                title: "Build an email list",
                description: "Instagram followers are rented; email subscribers are owned. Use your bio link, Stories, and DM automations to drive followers to an email list where you control the relationship. Email lists convert 3-5x higher than social media for product launches and are immune to algorithm changes. Creators with large email lists command higher sponsorship rates because they can guarantee deliverability.",
              },
              {
                title: "Track and optimize relentlessly",
                description: "Use Instagram Insights and third-party analytics to track which content types, posting times, and topics generate the most engagement and revenue. Double down on what works. A/B test your DM scripts, affiliate pitches, and call-to-action placements. The creators who treat their Instagram like a business — tracking metrics weekly and optimizing continuously — consistently out-earn those who post casually.",
              },
            ]} />
          </ContentSection>

          {/* Section 6: Which Instagram Niches Pay the Most? */}
          <ContentSection>
            <SectionHeading>Which Instagram Niches Pay the Most?</SectionHeading>
            <Paragraph className="mb-4">
              Not all Instagram niches are created equal when it comes to earning potential. The amount brands are willing to pay is directly tied to the customer lifetime value in that industry. Here are the top-paying niches in 2026, ranked by typical CPM (cost per thousand impressions) and per-post rates.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              {
                icon: "\u{1F4B0}",
                title: "Finance & Business (Highest CPM)",
                description: "Financial services, investing apps, business tools, and B2B SaaS products pay the highest rates on Instagram — often 2x the standard CPM. A single customer acquired through a finance influencer might be worth $500-$5,000+ in lifetime value, which is why brands in this space are willing to pay premium rates.",
              },
              {
                icon: "\u{1F4BB}",
                title: "Tech & SaaS",
                description: "Technology companies, software-as-a-service products, and gaming brands pay approximately 1.5x standard rates. Tech audiences tend to have higher disposable income, and SaaS products have strong recurring revenue models that justify higher customer acquisition costs.",
              },
              {
                icon: "\u{1F3E5}",
                title: "Health & Wellness",
                description: "The health and wellness market is massive, encompassing supplements, fitness programs, mental health apps, and organic products. Rates run about 1.2x standard, with fitness creators and nutritionists often earning additional income through their own coaching programs and digital products.",
              },
              {
                icon: "\u{1F457}",
                title: "Fashion & Beauty",
                description: "Fashion and beauty remain among the most competitive and well-established niches on Instagram, with rates around 1.3x standard. Top fashion and beauty creators earn significant income through a combination of brand deals, affiliate commissions from product links, and their own product lines.",
              },
              {
                icon: "\u2708\uFE0F",
                title: "Travel & Luxury",
                description: "Travel and luxury lifestyle creators earn approximately 1.2x standard rates, but the real perks in this niche go beyond cash. Hotel stays, business-class flights, resort packages, and luxury product gifting can add $5,000-$50,000+ in annual value on top of monetary compensation.",
              },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!result ? (
            <div className="space-y-4">
              <NumberField label="Number of Followers" value={followers} onChange={setFollowers} placeholder="e.g. 10000" required min={100} />
              <NumberField label="Engagement Rate (%)" value={engagementRate} onChange={setEngagementRate} placeholder="e.g. 3.5" required min={0.1} max={100} step={0.1} />
              <SelectField
                label="Content Niche"
                value={niche}
                onChange={setNiche}
                options={NICHES.map(n => ({ value: n.value, label: n.label }))}
                required
              />
              <SubmitButton onClick={calculate} disabled={!followers || !engagementRate}>Calculate Earnings</SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Creator Tier</p>
                <p className="text-lg font-bold text-neutral-900 dark:text-white">{result.tier}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <ToolResultCard label="Per Sponsored Post" value={fmt(result.perPost)} variant="primary" />
                <ToolResultCard label="Per Story/Reel" value={fmt(result.perStory)} />
                <ToolResultCard label="Monthly Potential" value={fmt(result.monthlyPotential)} variant="success" />
              </div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
                Estimates based on industry averages. Actual rates vary by audience quality and negotiation.
              </p>
              <ResetButton onClick={reset} />
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
